//-- Copyright (c) 2021 TMT Insights Group, LLC
import PolarisIcon from '@/components/_common/PolarisIcon';
import ClickCopy from '@/components/_common/ClickCopy/ClickCopy';
import styled from '@emotion/styled';
import { Fade, darken } from '@mui/material';
import { ReactNode, useState } from 'react';
import { insertWordBreaks } from '@/_utils/utils';

interface FileAccordionProps {
  children: ReactNode;
  icon: string;
  fileName: string;
  color: string;
}

const DeliverableFile = ({ icon, fileName, color, children }: FileAccordionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Wrapper>
      <LineItem onClick={() => setIsOpen(!isOpen)}>
        <Left>
          <FileTypeIcon icon={icon} bg={color} />
          <ClickCopy copyText={fileName} placement="bottom">
            <FileName exists={+!!fileName}>{fileName ? insertWordBreaks(fileName) : 'Filename not yet generated'}</FileName>
          </ClickCopy>
        </Left>
        <DropDownIcon icon="down_filled" open={+isOpen} height={16} />
      </LineItem>
      <Fade in={isOpen} timeout={300} unmountOnExit>
        <Dropdown>{children}</Dropdown>
      </Fade>
    </Wrapper>
  );
};

export default DeliverableFile;

const Wrapper = styled.div`
  position: relative;
  margin: ${({ theme }) => theme.spacing(1, 0)};
`;

const Dropdown = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const LineItem = styled.div`
  background: ${({ theme }) => theme.palette.background.default};
  padding: ${({ theme }) => theme.spacing(1.5)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: ${({ theme }) => (theme.palette.mode === 'light' ? `1px solid ${darken(theme.palette.background.default, 0.2)}` : 'none')};
`;

const FileTypeIcon = styled(PolarisIcon)<{ bg: string }>`
  background: ${({ bg }) => bg};
  border-radius: 6px;
  padding: 3px;
  color: ${({ theme }) => (theme.palette.mode === 'light' ? `#EBEDF6` : '#15161AB8')};
  min-width: 22px;
  min-height: 22px;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1.5)};
  max-width: 100%;
`;

const FileName = styled.span<{ exists: number }>`
  font-style: ${({ exists }) => (exists ? 'normal' : 'italic')};
  opacity: ${({ exists }) => (exists ? 1 : 0.5)};
  word-wrap: break-word;
  white-space: normal;
  max-width: calc(100% - 25px);
`;

const DropDownIcon = styled(PolarisIcon)<{ open: number }>`
  opacity: 0.3;
  padding: 0px;
  transform: ${({ open }) => (open ? `rotate(180deg)` : `rotate(0deg)`)};
`;
